import { useEffect, useRef } from 'react';

import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import {
  ProductWizardStep,
  WizardRouteRedirectTarget,
} from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { InteractionManager } from 'react-native';

import { routes } from 'utils/routing';

/**
 * Applies wizard route redirects for invalid URL params.
 *
 * @returns `true` when the screen must not render until the redirect completes.
 */
export function useProductDetailRouteRedirect(): boolean {
  const router = useRouter();
  const { routeRedirect } = useProductWizardFlow();

  const pathname = usePathname();

  const params = useLocalSearchParams();
  const paramsRef = useRef(params);

  // We store params in a ref because:
  // 1. We only want the redirect to run once when `routeRedirect` changes — not on every render.
  // 2. `useLocalSearchParams()` gives us a new object on many renders even when the URL
  //    didn't change. Putting `params` in the effect deps would re-trigger the redirect.
  // 3. We update the ref on every render so the delayed callback always reads the
  //    latest params without restarting the effect.
  paramsRef.current = params;

  useEffect(() => {
    if (!routeRedirect) {
      return;
    }

    const task = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        if (routeRedirect === WizardRouteRedirectTarget.Home) {
          router.replace({
            pathname,
            params: {
              ...paramsRef.current,
              step: ProductWizardStep.ProductHome,
              slotKey: '',
              pickerAspectKey: '',
            },
          });
        } else if (routeRedirect === WizardRouteRedirectTarget.Cart) {
          router.replace(routes.cart);
        } else if (routeRedirect === WizardRouteRedirectTarget.Menu) {
          router.replace(routes.menu);
        }
      });
    });

    return () => task.cancel();
  }, [routeRedirect, router, pathname]);

  return routeRedirect !== null;
}
